import {
  InvalidateQueryFilters,
  UseMutationOptions,
  UseMutationResult,
  UseQueryResult,
} from '@tanstack/react-query';
import { PageType, SourceType } from 'api/page-template/types';
import { useCallback } from 'react';
import {
  CreatePageTemplateMutationOptions as CreateMutationOptions,
  UpdatePageTemplateMutationOptions as UpdateMutationOptions,
  useCreatePageTemplateMutation as createPageTemplateMutation,
  useDeletePageTemplateMutation as deletePageTemplateMutation,
  useUpdatePageTemplateMutation as updatePageTemplateMutation,
  useUpdatePageTemplateOrderMutation as updatePageTemplateOrderMutation,
  useUpdatePageTemplateStatusMutation as updatePageTemplateStatusMutation,
} from './mutations';
import {
  GetAllPageTemplateQueryOption,
  GetPageTemplateQueryOption,
  useGetAllPageTemplatesQuery,
  useGetPageTemplateQuery,
  useInvalidateAllPageTemplatesQuery,
  useInvalidatePageTemplateQuery,
} from './queries';

export interface PageTemplateFactoryConfig<
  TPageType extends PageType,
  TSourceType extends SourceType,
  TTemplate,
  TTemplateLite,
  TCreatePayload,
  TUpdatePayload,
> {
  pageType: TPageType;
  sourceType: TSourceType;
  templateType: TTemplate;
  templateLiteType: TTemplateLite;
  createPayloadType: TCreatePayload;
  updatePayloadType: TUpdatePayload;
}

export interface PageTemplateHooks<
  TTemplate,
  TTemplateLite,
  TCreatePayload,
  TUpdatePayload,
> {
  useGetAllTemplatesQuery: (
    queryOptions?: GetAllPageTemplateQueryOption<any, any>,
  ) => UseQueryResult<TTemplateLite[], Error>;

  useGetTemplateQuery: (
    templateId: number,
    queryOptions?: GetPageTemplateQueryOption<any, any>,
  ) => UseQueryResult<TTemplate, Error>;

  useCreateTemplateMutation: (
    options?: Omit<CreateMutationOptions<any, any>, 'onSuccess'>,
  ) => UseMutationResult<TTemplate, Error, TCreatePayload>;

  useUpdateTemplateMutation: (
    options?: UpdateMutationOptions<any, any>,
  ) => UseMutationResult<TTemplate, Error, TUpdatePayload>;

  useDeleteTemplateMutation: (
    queryOptions?: Omit<
      UseMutationOptions<void, Error, number>,
      'mutationFn' | 'onSuccess'
    >,
  ) => UseMutationResult<void, Error, number>;

  useUpdateTemplateOrderMutation: (
    queryOptions?: Omit<
      UseMutationOptions<void, Error, { templateId: number; order: number }>,
      'mutationFn' | 'onSuccess'
    >,
  ) => UseMutationResult<void, Error, { templateId: number; order: number }>;

  useUpdateTemplateStatusMutation: (
    queryOptions?: Omit<
      UseMutationOptions<void, Error, { templateId: number; status: boolean }>,
      'mutationFn' | 'onSuccess'
    >,
  ) => UseMutationResult<void, Error, { templateId: number; status: boolean }>;
}

// Custom hook for invalidation handler
const useInvalidationHandler = (pageType: PageType, sourceType: SourceType) => {
  const invalidateAllPageTemplates = useInvalidateAllPageTemplatesQuery(
    pageType,
    sourceType,
  );

  return useCallback(
    async (options?: InvalidateQueryFilters) => {
      await invalidateAllPageTemplates(options);
    },
    [invalidateAllPageTemplates],
  );
};

export function createPageTemplateHooks<
  TPageType extends PageType,
  TSourceType extends SourceType,
  TTemplate,
  TTemplateLite,
  TCreatePayload,
  TUpdatePayload,
>(
  config: PageTemplateFactoryConfig<
    TPageType,
    TSourceType,
    TTemplate,
    TTemplateLite,
    TCreatePayload,
    TUpdatePayload
  >,
): PageTemplateHooks<TTemplate, TTemplateLite, TCreatePayload, TUpdatePayload> {
  const { pageType, sourceType } = config;

  const useGetAllTemplatesQuery = (
    queryOptions?: GetAllPageTemplateQueryOption<TPageType, TSourceType>,
  ): UseQueryResult<TTemplateLite[], Error> =>
    useGetAllPageTemplatesQuery(
      pageType,
      sourceType,
      queryOptions,
    ) as unknown as UseQueryResult<TTemplateLite[], Error>;

  const useGetTemplateQuery = (
    templateId: number,
    queryOptions?: GetPageTemplateQueryOption<TPageType, TSourceType>,
  ): UseQueryResult<TTemplate, Error> =>
    useGetPageTemplateQuery(
      templateId,
      queryOptions,
    ) as unknown as UseQueryResult<TTemplate, Error>;

  const useCreateTemplateMutation = (
    options?: Omit<CreateMutationOptions<TPageType, TSourceType>, 'onSuccess'>,
  ): UseMutationResult<TTemplate, Error, TCreatePayload> => {
    const invalidateAllTemplates = useInvalidationHandler(pageType, sourceType);

    return createPageTemplateMutation<TPageType, TSourceType>({
      onSuccess: () => invalidateAllTemplates({ refetchType: 'all' }),
      ...options,
    }) as unknown as UseMutationResult<TTemplate, Error, TCreatePayload>;
  };

  const useUpdateTemplateMutation = (
    options?: UpdateMutationOptions<TPageType, TSourceType>,
  ): UseMutationResult<TTemplate, Error, TUpdatePayload> => {
    const invalidateAllPageTemplates = useInvalidateAllPageTemplatesQuery(
      pageType,
      sourceType,
    );
    const invalidatePageTemplate = useInvalidatePageTemplateQuery();

    return updatePageTemplateMutation<TPageType, TSourceType>({
      onSuccess: async (_, data) => {
        await invalidateAllPageTemplates({ refetchType: 'all' });
        await invalidatePageTemplate(data.templateId);
      },
      ...options,
    }) as unknown as UseMutationResult<TTemplate, Error, TUpdatePayload>;
  };

  const useDeleteTemplateMutation = (
    queryOptions?: Omit<
      UseMutationOptions<void, Error, number>,
      'mutationFn' | 'onSuccess'
    >,
  ): UseMutationResult<void, Error, number> => {
    const invalidateAllTemplates = useInvalidationHandler(pageType, sourceType);

    return deletePageTemplateMutation({
      onSuccess: () => invalidateAllTemplates(),
      ...queryOptions,
    });
  };

  const useUpdateTemplateOrderMutation = (
    queryOptions?: Omit<
      UseMutationOptions<void, Error, { templateId: number; order: number }>,
      'mutationFn' | 'onSuccess'
    >,
  ): UseMutationResult<void, Error, { templateId: number; order: number }> => {
    const invalidateAllTemplates = useInvalidationHandler(pageType, sourceType);

    return updatePageTemplateOrderMutation({
      onSuccess: () => invalidateAllTemplates(),
      ...queryOptions,
    });
  };

  const useUpdateTemplateStatusMutation = (
    queryOptions?: Omit<
      UseMutationOptions<void, Error, { templateId: number; status: boolean }>,
      'mutationFn' | 'onSuccess'
    >,
  ): UseMutationResult<
    void,
    Error,
    { templateId: number; status: boolean }
  > => {
    const invalidateAllTemplates = useInvalidationHandler(pageType, sourceType);

    return updatePageTemplateStatusMutation({
      onSuccess: () => invalidateAllTemplates(),
      ...queryOptions,
    });
  };

  return {
    useGetAllTemplatesQuery,
    useGetTemplateQuery,
    useCreateTemplateMutation,
    useUpdateTemplateMutation,
    useDeleteTemplateMutation,
    useUpdateTemplateOrderMutation,
    useUpdateTemplateStatusMutation,
  };
}
